
"use client";

import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { CheckCircle, CreditCard, Heart, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function PricingClient() {
    return (
        <div className="flex flex-col min-h-screen bg-background">
            <Header />
            <main className="flex-grow pt-32 pb-24">
                <div className="container mx-auto px-6 max-w-5xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="text-center md:text-left mb-16">
                            <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-black">
                                Transparency
                            </Badge>
                            <h1 className="font-headline text-5xl md:text-7xl font-black text-foreground mb-6 tracking-tight">
                                Pricing & <span className="text-primary italic font-normal">Payment</span>
                            </h1>
                            <p className="text-xl text-muted-foreground font-medium max-w-2xl leading-relaxed">
                                We believe in complete transparency. Our pricing reflects the dedication and premium care every puppy receives.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-20">
                            <Card className="rounded-[2.5rem] shadow-soft border-none bg-card overflow-hidden relative group hover:shadow-soft-hover transition-all duration-500">
                                <div className="absolute top-0 left-0 w-full h-2 bg-primary/20" />
                                <CardHeader className="p-10 pb-4">
                                    <CardTitle className="text-3xl font-headline font-bold text-foreground">Toy Poodle</CardTitle>
                                    <CardDescription className="text-base lowercase font-medium">Curly, intelligent, and hypoallergenic</CardDescription>
                                </CardHeader>
                                <CardContent className="p-10 pt-2">
                                    <p className="text-5xl font-black text-primary tracking-tighter mb-4">$1,500 <span className="text-2xl text-muted-foreground font-medium">–</span> $2,500</p>
                                    <p className="text-sm text-muted-foreground">Price varies based on size, color markings, and pedigree.</p>
                                </CardContent>
                            </Card>

                            <Card className="rounded-[2.5rem] shadow-soft border-none bg-card overflow-hidden relative group hover:shadow-soft-hover transition-all duration-500">
                                <div className="absolute top-0 left-0 w-full h-2 bg-secondary" />
                                <CardHeader className="p-10 pb-4">
                                    <CardTitle className="text-3xl font-headline font-bold text-foreground">Toy Maltipoo</CardTitle>
                                    <CardDescription className="text-base lowercase font-medium">Playful, affectionate, and soft-coated</CardDescription>
                                </CardHeader>
                                <CardContent className="p-10 pt-2">
                                    <p className="text-5xl font-black text-primary tracking-tighter mb-4">$1,500 <span className="text-2xl text-muted-foreground font-medium">–</span> $2,500</p>
                                    <p className="text-sm text-muted-foreground">Price varies based on size, generation (F1/F1b), and coat type.</p>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="grid md:grid-cols-12 gap-10">
                            {/* Payment Methods Section */}
                            <section className="md:col-span-5">
                                <Card className="h-full border-border/50 shadow-sm bg-secondary/20">
                                    <CardHeader>
                                        <div className="w-12 h-12 bg-background rounded-full flex items-center justify-center mb-4 text-foreground shadow-sm">
                                            <CreditCard className="w-6 h-6" />
                                        </div>
                                        <CardTitle className="text-2xl font-bold text-foreground">Payment Methods</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                                            We accept payment via approved methods to ensure security for both parties.
                                        </p>
                                        <ul className="space-y-3">
                                            <li className="flex items-center gap-3 text-foreground font-medium">
                                                <div className="w-2 h-2 rounded-full bg-primary" />
                                                Cash
                                            </li>
                                            <li className="flex items-center gap-3 text-foreground font-medium">
                                                <div className="w-2 h-2 rounded-full bg-primary" />
                                                Zelle
                                            </li>
                                            <li className="flex items-center gap-3 text-foreground font-medium">
                                                <div className="w-2 h-2 rounded-full bg-primary" />
                                                Apple Pay
                                            </li>
                                        </ul>
                                    </CardContent>
                                </Card>
                            </section>

                            {/* Value / What you get Section */}
                            <section className="md:col-span-7 space-y-12">
                                <div>
                                    <div className="mb-6">
                                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">What's included</h2>
                                        <p className="text-muted-foreground">Every puppy goes home with a solid foundation.</p>
                                    </div>

                                    <div className="grid gap-4">
                                        {[
                                            {
                                                icon: Heart,
                                                title: "Home Raised & Loved",
                                                desc: "Raised in our living room, not a kennel. Daily socialization with kids and household noises."
                                            },
                                            {
                                                icon: ShieldCheck,
                                                title: "Health Guarantee",
                                                desc: "Comprehensive 1-year genetic health guarantee and up-to-date vaccinations."
                                            },
                                            {
                                                icon: CheckCircle,
                                                title: "Lifetime Support",
                                                desc: "We are here for you for the life of your dog. Questions, advice, or just sharing photos."
                                            }
                                        ].map((item, i) => (
                                            <Card key={i} className="border-border shadow-sm hover:shadow-md transition-all duration-300">
                                                <CardContent className="flex items-start gap-5 p-6">
                                                    <div className="p-3 bg-secondary/50 rounded-xl text-primary shrink-0">
                                                        <item.icon className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-foreground text-lg mb-1">{item.title}</h3>
                                                        <p className="text-muted-foreground font-medium leading-relaxed">{item.desc}</p>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>

                                <div className="prose prose-stone max-w-none text-muted-foreground">
                                    <h3 className="text-2xl font-headline font-bold text-foreground mb-4">Understanding the Investment</h3>
                                    <p className="leading-relaxed mb-4">
                                        We understand that a responsibly raised puppy is a significant investment. The adoption fee reflects the immense care, high-quality nutrition, veterinary oversight, and time dedicated to raising each puppy properly.
                                    </p>
                                    <p className="leading-relaxed">
                                        This includes premium nutrition and a rich enrichment curriculum. We do not cut corners because we are raising family members, not just pets.
                                    </p>

                                    <h3 className="text-2xl font-headline font-bold text-foreground mt-8 mb-4">Reservation Policy</h3>
                                    <p className="leading-relaxed">
                                        A non-refundable deposit is required to reserve a specific puppy. This deposit is applied toward the final adoption fee. The remaining balance is due at pickup. If, for any reason, a puppy becomes unavailable (due to health assessment), your deposit may be transferred to a future puppy.
                                    </p>
                                </div>
                            </section>
                        </div>
                    </motion.div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
